import { useMemo } from "react";
import { ComposableMap, Geographies, Geography, Line, Marker } from "react-simple-maps";
import { Globe } from "lucide-react";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// [lng, lat] centroids for the jurisdictions used across value chains.
const CENTROIDS = {
  GB: [-1.5, 52.5], DE: [10.4, 51.1], TW: [121, 23.7], CN: [104, 35], US: [-98, 39],
  JP: [138, 36], KR: [127.8, 36.5], IN: [79, 22], VN: [106, 16], RU: [90, 61],
  MX: [-102, 23], PL: [19, 52], CZ: [15.5, 49.8], SG: [103.8, 1.35], MY: [102, 4],
  TH: [101, 15], BR: [-51, -10], FR: [2.2, 46.2], NL: [5.3, 52.1], IT: [12.5, 42.8],
  ES: [-3.7, 40.4], CA: [-106, 56], AU: [134, -25], CH: [8.2, 46.8], SE: [15, 62],
  TR: [35, 39], AE: [54, 24], SA: [45, 24], ZA: [24, -29], CL: [-71, -30], ID: [113, -2],
};

const tone = (composite) =>
  composite >= 70 ? "#dc2626" : composite >= 45 ? "#d97706" : "#7737F6";

export const GeoArcMap = ({ chains = [] }) => {
  const { arcs, nodes } = useMemo(() => {
    const arcList = [];
    const nodeSet = {};
    chains.forEach((c, ci) => {
      const path = c.country_path || [];
      path.forEach((code) => {
        if (CENTROIDS[code]) nodeSet[code] = true;
      });
      for (let i = 0; i < path.length - 1; i++) {
        const a = CENTROIDS[path[i]];
        const b = CENTROIDS[path[i + 1]];
        if (a && b) {
          arcList.push({
            key: `${ci}-${i}`,
            from: a,
            to: b,
            color: tone(c.composite_after ?? c.composite ?? c.composite_before ?? 40),
          });
        }
      }
    });
    return { arcs: arcList, nodes: Object.keys(nodeSet) };
  }, [chains]);

  return (
    <div className="rounded-md border border-black/5 bg-white p-5" data-testid="geo-arc-map">
      <h2 className="ek-eyebrow flex items-center gap-1">
        <Globe size={12} /> Value-chain world map
      </h2>
      <p className="mt-1 text-sm text-ek-ink/65">
        Cross-border flows arc between jurisdictions — colour reflects each chain&apos;s stress
        (purple → amber → red).
      </p>
      <div className="mt-3">
        <ComposableMap
          projectionConfig={{ scale: 150 }}
          width={900}
          height={430}
          style={{ width: "100%", height: "auto" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#F1F1F4"
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#E6E0F7", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>
          {arcs.map((a) => (
            <Line
              key={a.key}
              from={a.from}
              to={a.to}
              stroke={a.color}
              strokeWidth={1.6}
              strokeOpacity={0.6}
              strokeLinecap="round"
            />
          ))}
          {nodes.map((code) => (
            <Marker key={code} coordinates={CENTROIDS[code]} data-testid={`geo-node-${code}`}>
              <circle r={4} fill="#1a1a1a" fillOpacity={0.85} stroke="#fff" strokeWidth={1} />
              <text
                textAnchor="middle"
                y={-7}
                style={{ fontFamily: "Work Sans, sans-serif", fontSize: 8.5, fontWeight: 700, fill: "#1a1a1a", pointerEvents: "none" }}
              >
                {code}
              </text>
            </Marker>
          ))}
        </ComposableMap>
      </div>
    </div>
  );
};
